import { History, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Capacitor } from '@capacitor/core'

import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/auth'
import { apiClient } from '@/lib/api/client'
import { queryKeys, useEvent } from '@/lib/api/queries'
import type {
  ChatMessage,
  ChatResolutionStatus,
  ChatStreamEvent,
  ToolCall,
} from '@/lib/api/support-chat-types'

import { ChatInput } from './chat-input'
import { ChatMessageList } from './chat-message-list'
import { EndSessionPrompt } from './end-session-prompt'
import { SessionHistoryList } from './session-history-list'

interface SupportChatDrawerProps {
  eventId: number
  /** Whether the drawer is currently visible. When false, the drawer is
   *  kept mounted (preserving session state) but hidden via display:none.
   *  Lets the operator minimize without ending the conversation. */
  isOpen: boolean
  /** Minimize handler — hides the drawer but does NOT end the session.
   *  The X in the header and Escape both fire this. */
  onClose: () => void
}

export function SupportChatDrawer({ eventId, isOpen, onClose }: SupportChatDrawerProps) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const { data: eventData } = useEvent(eventId)
  const [sessionId, setSessionId] = useState<number | null>(null)
  /** True when the active session has been ended on the backend. Input is
   *  disabled in this state — the operator is reviewing a closed transcript,
   *  not chatting. Toggle is set by `loadExistingSession` based on the
   *  session's `ended_at`. */
  const [sessionEnded, setSessionEnded] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [initError, setInitError] = useState<string | null>(null)
  /** Spinner state for both fresh-create and resume-existing flows. */
  const [isLoadingSession, setIsLoadingSession] = useState(true)
  const [showEndPrompt, setShowEndPrompt] = useState(false)
  const [composerPrefill, setComposerPrefill] = useState<string | undefined>(undefined)
  /** Toggle between live chat and history list. History is loaded lazily by
   *  `SessionHistoryList` (which calls the list endpoint via react-query). */
  const [view, setView] = useState<'current' | 'history'>('current')
  const streamAbortRef = useRef<AbortController | null>(null)

  /** Spin up a brand-new session. Used on initial mount and when the
   *  operator clicks "New chat" from the history panel. Aborts any
   *  in-flight stream and clears local message state so the new session
   *  starts clean. */
  const createNewSession = useCallback(async () => {
    if (!token) return
    streamAbortRef.current?.abort()
    setInitError(null)
    setIsLoadingSession(true)
    setMessages([])
    setSessionId(null)
    setSessionEnded(false)
    setView('current')
    try {
      const session = await apiClient.createSupportChatSession(token, eventId)
      setSessionId(session.id)
    } catch (err) {
      setInitError(err instanceof Error ? err.message : 'Failed to start chat')
    } finally {
      setIsLoadingSession(false)
    }
  }, [token, eventId])

  /** Load an existing session (with its full message history) from the
   *  backend. Sets `sessionEnded` so the input is disabled if the session
   *  was already closed — the operator views past chats read-only.
   *  Resumable (unended) sessions just resume normally with input enabled. */
  const loadExistingSession = useCallback(
    async (id: number) => {
      if (!token) return
      streamAbortRef.current?.abort()
      setInitError(null)
      setIsLoadingSession(true)
      setMessages([])
      setSessionId(null)
      setSessionEnded(false)
      setView('current')
      try {
        const session = await apiClient.getSupportChatSession(token, id)
        setSessionId(session.id)
        setSessionEnded(session.ended_at !== null)
        setMessages(session.messages ?? [])
      } catch (err) {
        setInitError(err instanceof Error ? err.message : 'Failed to load chat')
      } finally {
        setIsLoadingSession(false)
      }
    },
    [token],
  )

  // Initial mount: auto-create a session. Subsequent navigation (resume,
  // new chat) is driven by user-triggered handlers, not this effect.
  useEffect(() => {
    createNewSession()
    return () => {
      streamAbortRef.current?.abort()
    }
  }, [createNewSession])

  const patchPendingActionResult = useCallback(
    (pendingActionId: number, newResult: import('@/lib/api/support-chat-types').ToolResult) => {
      setMessages((prev) =>
        prev.map((msg) => {
          if (!msg.tool_calls) return msg
          const updated = msg.tool_calls.map((tc) => {
            const data = tc.result?.ok ? (tc.result.data as Record<string, unknown>) : null
            if (data && data.pending_action_id === pendingActionId) {
              return { ...tc, result: newResult }
            }
            return tc
          })
          return { ...msg, tool_calls: updated }
        }),
      )
    },
    [],
  )

  const handleConfirmAction = useCallback(
    async (pendingActionId: number) => {
      if (!token || !sessionId) return
      const { result } = await apiClient.confirmPendingAction(token, sessionId, pendingActionId)
      patchPendingActionResult(pendingActionId, result)
    },
    [token, sessionId, patchPendingActionResult],
  )

  const handleCancelAction = useCallback(
    async (pendingActionId: number) => {
      if (!token || !sessionId) return
      const { result } = await apiClient.cancelPendingAction(token, sessionId, pendingActionId)
      patchPendingActionResult(pendingActionId, result)
    },
    [token, sessionId, patchPendingActionResult],
  )

  // Abort the in-flight Anthropic stream when the user clicks Stop. Backend
  // generator notices the closed connection and unwinds; assistant message
  // is marked `[stopped]` in the catch block above so the operator sees what
  // got through before the abort.
  const handleStop = useCallback(() => {
    streamAbortRef.current?.abort()
  }, [])

  // Minimize: hide the drawer but keep the session alive. The session row
  // in the DB stays open, messages stay in component state, streaming
  // continues (the parent keeps this drawer mounted while `isOpen` toggles).
  const handleMinimize = useCallback(() => {
    onClose()
  }, [onClose])

  // End-session: explicit user action. Shows the resolution prompt if there
  // was any conversation; otherwise ends silently and minimizes.
  const handleEndRequest = useCallback(() => {
    if (sessionId && messages.length > 0) {
      setShowEndPrompt(true)
      return
    }
    finishAndClose(null)
  }, [messages.length, sessionId])

  // Called after the resolution prompt submits (or by the no-messages
  // fast-path above). Aborts any in-flight stream, ends the session via
  // API, then minimizes — parent will unmount us on next event change.
  // Also invalidates the history list so the newly-ended session moves
  // into the closed-sessions group the next time the operator opens it.
  //
  // Sets `sessionEnded=true` synchronously so if the operator reopens the
  // drawer (without navigating away), the banner+disabled-input state is
  // visible immediately — no awkward "still typing into a dead session"
  // window while the end API call is in flight.
  const finishAndClose = useCallback(
    (resolution: ChatResolutionStatus | null) => {
      streamAbortRef.current?.abort()
      if (token && sessionId) {
        apiClient
          .endSupportChatSession(token, sessionId, resolution ?? undefined)
          .catch(() => {
            // Best-effort: ending shouldn't block on a failed API call.
          })
          .finally(() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.supportChatSessions(eventId) })
          })
      }
      setSessionEnded(true)
      setShowEndPrompt(false)
      onClose()
    },
    [onClose, sessionId, token, queryClient, eventId],
  )

  const handleResolutionSubmit = useCallback(
    async (resolution: ChatResolutionStatus | null) => {
      finishAndClose(resolution)
    },
    [finishAndClose],
  )

  // Escape minimizes (does NOT end). Only listen while the drawer is open
  // so other components on the page can use Escape too.
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleMinimize()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handleMinimize, isOpen])

  const sendMessage = useCallback(
    async (content: string) => {
      if (!token || !sessionId || isStreaming || sessionEnded) return

      const userMessage: ChatMessage = {
        id: Date.now(), // optimistic placeholder; replaced when session refetches
        session_id: sessionId,
        role: 'user',
        content,
        tool_calls: null,
        created_at: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, userMessage])

      const controller = new AbortController()
      streamAbortRef.current = controller
      setIsStreaming(true)

      // Placeholder assistant message we mutate as deltas arrive.
      let assistantId = -Date.now()
      let assistantContent = ''
      setMessages((prev) => [
        ...prev,
        {
          id: assistantId,
          session_id: sessionId,
          role: 'assistant',
          content: '',
          tool_calls: null,
          created_at: new Date().toISOString(),
        },
      ])

      try {
        for await (const event of apiClient.streamSupportChatMessage(
          token,
          sessionId,
          content,
          controller.signal,
        )) {
          handleStreamEvent(event)
        }
      } catch (err) {
        if (controller.signal.aborted) {
          // User-initiated stop. Keep whatever streamed so far and tack on a
          // small marker so they know the response was cut short.
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== assistantId) return m
              const trimmed = (m.content || '').trimEnd()
              return { ...m, content: trimmed ? `${trimmed}\n\n_[stopped]_` : '_[stopped]_' }
            }),
          )
          return
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: assistantContent || `Error: ${err instanceof Error ? err.message : 'stream failed'}` }
              : m,
          ),
        )
      } finally {
        setIsStreaming(false)
        streamAbortRef.current = null
      }

      function handleStreamEvent(event: ChatStreamEvent) {
        if (event.type === 'message_start') {
          assistantId = event.message_id
          setMessages((prev) =>
            prev.map((m) => (m.id < 0 && m.role === 'assistant' ? { ...m, id: event.message_id } : m)),
          )
        } else if (event.type === 'text_delta') {
          assistantContent += event.delta
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, content: assistantContent } : m)),
          )
        } else if (event.type === 'tool_call_start') {
          const newCall: ToolCall = { ...event.tool_call }
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, tool_calls: [...(m.tool_calls ?? []), newCall] }
                : m,
            ),
          )
        } else if (event.type === 'tool_call_result') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    tool_calls: (m.tool_calls ?? []).map((tc) =>
                      tc.id === event.tool_call_id ? { ...tc, result: event.result } : tc,
                    ),
                  }
                : m,
            ),
          )
        } else if (event.type === 'message_end') {
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? event.message : m)),
          )
        } else if (event.type === 'error') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content: `Error: ${event.message}` } : m,
            ),
          )
        }
      }
    },
    [token, sessionId, isStreaming],
  )

  const isNative = Capacitor.isNativePlatform()

  return (
    <aside
      role="dialog"
      aria-label="Support chat"
      aria-hidden={!isOpen}
      // Non-modal side panel — no backdrop. The drawer occupies the right
      // 28rem; everything to its left (page nav, dashboard, etc.) stays
      // fully interactive. Kept mounted across minimize cycles so the
      // session and message state survive. `pointer-events-none` when
      // hidden so the off-screen mount doesn't intercept clicks.
      className={cn(
        'fixed right-0 top-0 z-40 flex h-full w-full max-w-md flex-col overflow-hidden rounded-l-2xl border-l border-neutral-200 bg-white shadow-2xl transition-transform duration-200 ease-out dark:border-neutral-800 dark:bg-[#131313]',
        isOpen
          ? 'translate-x-0 pointer-events-auto'
          : 'translate-x-full pointer-events-none',
      )}
    >
      {/* Capacitor safe-area top inset — same idiom as navigation/header.tsx
          so the header clears the device status bar / notch. */}
      {isNative && (
        <div
          className="shrink-0 bg-white dark:bg-[#131313]"
          style={{ height: 'var(--safe-area-top)' }}
        />
      )}
      <header className="flex items-center justify-between border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {view === 'history' ? 'Chat history' : 'Chat'}
          </h2>
          <p className="truncate text-[11px] text-neutral-500 dark:text-neutral-400">
            {eventData?.name || `Event #${eventId}`}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {/* History toggle. Hidden while creating the very first session
              (no point showing history before the user even has a chat). */}
          {view === 'current' ? (
            <button
              type="button"
              onClick={() => setView('history')}
              aria-label="View chat history"
              title="Past chats"
              className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
            >
              <History className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setView('current')}
              aria-label="Back to current chat"
              title="Back"
              className="rounded-md px-2 py-1 text-[11px] font-medium text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
            >
              Back
            </button>
          )}
          {/* End session: only meaningful when viewing a live (non-ended)
              session that has at least started. Hidden in history view. */}
          {view === 'current' && sessionId !== null && !sessionEnded && (
            <button
              type="button"
              onClick={handleEndRequest}
              aria-label="End support session"
              title="End session"
              className="rounded-md px-2 py-1 text-[11px] font-medium text-neutral-500 hover:bg-neutral-100 hover:text-red-600 dark:hover:bg-neutral-800 dark:hover:text-red-400"
            >
              End session
            </button>
          )}
          <button
            type="button"
            onClick={handleMinimize}
            aria-label="Minimize support chat"
            title="Minimize"
            className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

        {view === 'history' ? (
          <SessionHistoryList
            eventId={eventId}
            onSelect={loadExistingSession}
            onStartNew={createNewSession}
          />
        ) : initError ? (
          <div className="m-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
            Couldn&apos;t start the chat: {initError}
          </div>
        ) : isLoadingSession || sessionId === null ? (
          <div className="flex flex-1 items-center justify-center text-sm text-neutral-500 dark:text-neutral-400">
            {messages.length > 0 ? 'Loading chat…' : 'Starting session…'}
          </div>
        ) : (
          <>
            {sessionEnded && (
              // Banner shown when the operator is viewing an already-ended
              // session from history. Input below stays disabled — backend
              // would reject new messages anyway (400 "Session has ended.").
              <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-2 text-[11px] text-neutral-600 dark:border-neutral-800 dark:bg-neutral-900/40 dark:text-neutral-400">
                This chat has ended.{' '}
                <button
                  type="button"
                  onClick={createNewSession}
                  className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                >
                  Start a new chat
                </button>
              </div>
            )}
            <ChatMessageList
              messages={messages}
              isStreaming={isStreaming}
              onConfirmAction={handleConfirmAction}
              onCancelAction={handleCancelAction}
              onQuickStart={(prefill) => setComposerPrefill(prefill)}
            />
            {!sessionEnded && (
              <ChatInput
                onSend={sendMessage}
                isStreaming={isStreaming}
                onStop={handleStop}
                prefill={composerPrefill}
                onPrefillConsumed={() => setComposerPrefill(undefined)}
              />
            )}
          </>
        )}

        {showEndPrompt && (
          <EndSessionPrompt
            onSubmit={handleResolutionSubmit}
            onCancel={() => setShowEndPrompt(false)}
          />
        )}
    </aside>
  )
}
